import { useCallback } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useWorldCupTournamentStandingsQuery } from '../../../src/worldcup/hooks/useWorldCupTournamentStandingsQuery';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { useCompetitionsListQuery } from '../../../src/competitions/hooks/useCompetitionsListQuery';
import { TournamentStandingsView } from './TournamentStandingsView';

/**
 * Standings container for a competition (specs/champions-league, Story 5).
 *
 * The query is already parameterized by competition; the route now serves any competition
 * whose resolved rules set `hasTournamentStandings`, and returns an empty `groups` until the
 * competition has actually played a match (AC-5.4).
 */
export function TournamentStandingsScreen() {
  const queryClient = useQueryClient();
  const { competitionId: rawCompetitionId } = useLocalSearchParams<{ competitionId: string }>();
  const competitionId = rawCompetitionId ?? '';

  const { data, isLoading, isError } = useWorldCupTournamentStandingsQuery(
    competitionId,
    competitionId !== '',
  );
  const { data: competitions } = useCompetitionsListQuery();
  const sport = competitions?.find((c) => c.id === competitionId)?.sport ?? null;

  const onRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.standings(competitionId) });
  }, [queryClient, competitionId]);

  return (
    <TournamentStandingsView
      groups={data?.groups ?? []}
      sport={sport}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
    />
  );
}
